import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiCorePackage } from '@wix/da-papi-types';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { BiData, PackagePickerClickBiEvent } from '@wix/da-bi/pkg/events';
import { CoreTypes } from '../../../../../types';
import { type PackagePickerStoreSlice } from '../../../../../../../redux/packagePicker/types';

interface Props {
  className?: string;
  coreType: CoreTypes;
  pickerMode: PackagePickerStoreSlice['pickerMode'];
  alreadyHasSubscription?: boolean;
  monthlyPackage: PapiCorePackage;
  yearlyPackage: PapiCorePackage;
  isRecommended: boolean;
  isCurrent: boolean;
  onSelect: (selected: number[]) => void;
}

const CardButton: React.FC<Props> = ({
  className,
  coreType,
  pickerMode,
  alreadyHasSubscription,
  monthlyPackage,
  yearlyPackage,
  isRecommended,
  isCurrent,
  onSelect,
}) => {
  const { t } = useTranslation();
  const isGift = pickerMode === 'gift';
  const isDisabled =
    monthlyPackage.status === 'disabled' ||
    (monthlyPackage.status === 'manage' && isGift);

  const buttonTextKey = useMemo(() => {
    if (isGift) {
      return 'packagePicker.coreOffer.cta.gift';
    }
    return isCurrent
      ? 'packagePicker.coreOffer.cta.extend'
      : isRecommended
      ? 'packagePicker.coreOffer.cta.upgradeNow'
      : 'packagePicker.coreOffer.cta.upgrade';
  }, [isGift, isRecommended, isCurrent]);

  const biCoreType = coreType === CoreTypes.CORE_ACCESS ? 'core' : coreType;
  let biButtonType = 'gift';
  if (pickerMode === 'buy') {
    biButtonType = monthlyPackage.status === 'manage' ? 'manage' : 'select';
  }

  return (
    <BrandedButton
      className={className}
      size={'large'}
      variant={'core-orange'}
      onClick={() =>
        onSelect([monthlyPackage.subproductId, yearlyPackage.subproductId])
      }
      disabled={isDisabled}
      biData={BiData<PackagePickerClickBiEvent>({
        evid: 501,
        button_type: biButtonType as any,
        core_type: biCoreType,
        give_core_or_become_core: alreadyHasSubscription
          ? 'upgrade_core'
          : pickerMode === 'gift'
          ? 'give_core'
          : 'become_core',
      })}
    >
      {t(buttonTextKey)}
    </BrandedButton>
  );
};
CardButton.displayName = 'CardButton';

export default CardButton;
